// PerformanceTimeline · Lot M5
// Liste des snapshots (period_index / revision), badge is_current, delta S vs S-1,
// bouton "Corriger" (supersede) sur la révision courante de chaque période.

import { useMemo } from "react";
import { RefreshCcw, TrendingUp, TrendingDown, Minus, FileText } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const fmtDay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short",
    });
  } catch { return "—"; }
};

const fmtNum = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v !== "number") return String(v);
  return new Intl.NumberFormat("fr-FR").format(v);
};

const fmtSeconds = (s) => {
  if (s === null || s === undefined) return "—";
  const h = Math.floor(s / 3600);
  const d = Math.floor(h / 24);
  if (d >= 1) return `${d} j`;
  return `${h} h`;
};

const KEY_METRICS = [
  { key: "views",       label: "Vues" },
  { key: "leads",       label: "Leads" },
  { key: "messages",    label: "Messages" },
  { key: "calls",       label: "Appels" },
  { key: "visits",      label: "Visites" },
  { key: "test_drives", label: "Essais" },
  { key: "offers",      label: "Offres" },
];

function Delta({ current, previous }) {
  if (current === null || current === undefined) return <span className="text-black/30">—</span>;
  if (previous === null || previous === undefined) {
    return <span className="text-black/40">—</span>;
  }
  const diff = current - previous;
  if (diff === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] text-black/40">
        <Minus size={10} /> 0
      </span>
    );
  }
  const positive = diff > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive ? "text-emerald-600" : "text-[#B91C1C]";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] ${color}`}>
      <Icon size={10} /> {positive ? "+" : ""}{fmtNum(diff)}
    </span>
  );
}

export default function PerformanceTimeline({
  snapshots = [],   // liste ASC ou DESC — on tri ici
  loading = false,
  onSupersede,
  isTerminal = false,
}) {
  // Sort: DESC by period_index, DESC by revision, current first per period.
  const sorted = useMemo(() =>
    [...snapshots].sort((a, b) => {
      if (a.period_index !== b.period_index) return b.period_index - a.period_index;
      return b.revision - a.revision;
    })
  , [snapshots]);

  // Map "previous current" for delta calc — la révision courante de la période P-1
  const prevByPeriod = useMemo(() => {
    const map = new Map();
    const currents = sorted.filter(s => s.is_current)
      .sort((a, b) => a.period_index - b.period_index);
    for (let i = 1; i < currents.length; i++) {
      map.set(currents[i].period_index, currents[i - 1]);
    }
    return map;
  }, [sorted]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-black/[0.02] p-6 text-center text-[13px] text-black/40"
           data-testid="perf-timeline-loading">
        Chargement des snapshots…
      </div>
    );
  }

  if (!sorted.length) {
    return (
      <div className="rounded-2xl bg-black/[0.02] p-6 text-center text-[13px] text-black/40"
           data-testid="perf-timeline-empty">
        Aucun snapshot capturé pour l&apos;instant.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="perf-timeline">
      {sorted.map(snap => {
        const isCurrent = !!snap.is_current;
        const isSuperseded = !isCurrent;
        const previous = prevByPeriod.get(snap.period_index);
        const platforms = Object.keys(snap.by_platform || {});
        return (
          <article
            key={snap.id}
            className={`rounded-2xl p-5 transition ${
              isCurrent ? "bg-white/80" : "bg-black/[0.02]"
            }`}
            style={{
              boxShadow: isCurrent
                ? "inset 0 0 0 1px rgba(0,0,0,0.06)"
                : "inset 0 0 0 1px rgba(0,0,0,0.03)",
            }}
            data-testid={`perf-snap-${snap.period_index}-r${snap.revision}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10.5px] uppercase tracking-[0.14em] text-black/45">
                    Période
                  </span>
                  <span className="font-mono text-[15px] font-semibold text-black">
                    #{snap.period_index}
                  </span>
                  <span className="rounded-full bg-black/[0.06] px-2 py-0.5 text-[10.5px] uppercase tracking-[0.14em] text-black/60">
                    rev {snap.revision}
                  </span>
                  {isCurrent ? (
                    <span
                      className="rounded-full bg-[#C8FF2F]/40 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.14em] text-black"
                      data-testid={`perf-badge-current-${snap.period_index}`}
                    >
                      courante
                    </span>
                  ) : (
                    <span
                      className="rounded-full bg-black/10 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.14em] text-black/50"
                      data-testid={`perf-badge-superseded-${snap.id}`}
                    >
                      supersédée
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[12px] text-black/55">
                  {fmtDay(snap.period_start)} → {fmtDay(snap.period_end)}
                  {" · "}capturé {fmtDate(snap.captured_at)}
                  {snap.created_by_name ? ` par ${snap.created_by_name}` : ""}
                </div>
                {isSuperseded && (
                  <div className="mt-0.5 text-[11.5px] text-black/40">
                    Supersédée le {fmtDate(snap.superseded_at)}
                  </div>
                )}
              </div>
              {isCurrent && !isTerminal && (
                <button
                  onClick={() => onSupersede?.(snap)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[12px] font-medium text-black/70 hover:text-black"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
                  data-testid={`perf-supersede-btn-${snap.period_index}`}
                >
                  <RefreshCcw size={11} /> Corriger
                </button>
              )}
            </div>

            {/* Contexte capture */}
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 text-[11.5px]">
              <div>
                <div className="text-black/40 uppercase tracking-[0.12em] text-[10px]">
                  Prix affiché
                </div>
                <div className="mt-0.5 font-mono text-black">
                  {snap.asking_price_at_capture != null
                    ? fmtNum(snap.asking_price_at_capture) + " " + (snap.currency || "EUR")
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-black/40 uppercase tracking-[0.12em] text-[10px]">
                  Net vendeur
                </div>
                <div className="mt-0.5 font-mono text-black">
                  {snap.net_seller_at_capture != null
                    ? fmtNum(snap.net_seller_at_capture) + " " + (snap.currency || "EUR")
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-black/40 uppercase tracking-[0.12em] text-[10px]">
                  Actif période
                </div>
                <div className="mt-0.5 font-mono text-black">
                  {fmtSeconds(snap.active_seconds_in_period)}
                  {snap.partial_publication_period && (
                    <span className="ml-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] uppercase tracking-[0.12em] text-amber-700">
                      partiel
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-black/40 uppercase tracking-[0.12em] text-[10px]">
                  Canaux
                </div>
                <div className="mt-0.5 text-black">
                  {(snap.active_channels_at_capture || []).join(", ") || "—"}
                </div>
              </div>
            </div>

            {/* Totaux période + delta S-1 */}
            <div className="mt-4 rounded-xl bg-black/[0.02] p-3">
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-black/50">
                Totaux période — S vs S-1
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 md:grid-cols-7">
                {KEY_METRICS.map(m => {
                  const cur = snap.totals_period?.[m.key];
                  const prev = previous?.totals_period?.[m.key];
                  return (
                    <div key={m.key} className="rounded-lg bg-white/70 px-2 py-1.5"
                         data-testid={`perf-total-${m.key}-p${snap.period_index}`}>
                      <div className="text-[9.5px] uppercase tracking-[0.12em] text-black/45">
                        {m.label}
                      </div>
                      <div className="mt-0.5 font-mono text-[13px] text-black">
                        {fmtNum(cur)}
                      </div>
                      {isCurrent && previous && (
                        <div className="mt-0.5"><Delta current={cur} previous={prev} /></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ratios */}
            {snap.ratios_period && Object.values(snap.ratios_period).some(v => v != null) && (
              <div className="mt-2 flex flex-wrap gap-3 text-[11.5px] text-black/60">
                {snap.ratios_period.lead_per_view != null && (
                  <span>lead/vue&nbsp;
                    <b className="font-mono text-black">
                      {(snap.ratios_period.lead_per_view * 100).toFixed(2)}%
                    </b>
                  </span>
                )}
                {snap.ratios_period.visit_per_lead != null && (
                  <span>visite/lead&nbsp;
                    <b className="font-mono text-black">
                      {(snap.ratios_period.visit_per_lead * 100).toFixed(2)}%
                    </b>
                  </span>
                )}
                {snap.ratios_period.offer_per_visit != null && (
                  <span>offre/visite&nbsp;
                    <b className="font-mono text-black">
                      {(snap.ratios_period.offer_per_visit * 100).toFixed(2)}%
                    </b>
                  </span>
                )}
              </div>
            )}

            {/* Plateformes */}
            {platforms.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {platforms.map(ch => {
                  const p = snap.by_platform[ch];
                  return (
                    <span
                      key={ch}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] uppercase tracking-[0.12em] ${
                        p?.has_counter_reset
                          ? "bg-amber-500/15 text-amber-800"
                          : "bg-black/[0.05] text-black/60"
                      }`}
                      data-testid={`perf-chip-${ch}-p${snap.period_index}`}
                    >
                      {ch}
                      {p?.has_counter_reset && " · reset"}
                    </span>
                  );
                })}
              </div>
            )}

            {snap.notes && (
              <div className="mt-3 flex items-start gap-1.5 rounded-xl bg-black/[0.02] px-3 py-2 text-[12px] text-black/60">
                <FileText size={11} className="mt-0.5 shrink-0" />
                <span>{snap.notes}</span>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
